// src/features/pages/admin/roles/PermissionPicker.tsx
// Searchable permission catalog, grouped by service, with per-service and
// per-permission checkboxes and a running "selected" count. Selection is a flat
// set of permission keys (the shape POST/PATCH /rbac/roles expects).
import React, { useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import type { RbacCatalog } from '@/services/rbacService';

interface Props {
  catalog: RbacCatalog;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

const prettyService = (s: string) =>
  s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const PermissionPicker: React.FC<Props> = ({ catalog, selected, onChange }) => {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const q = query.trim().toLowerCase();
  const services = useMemo(() => {
    if (!q) return catalog.services;
    return catalog.services
      .map((s) => ({
        ...s,
        permissions: s.permissions.filter(
          (p) =>
            p.key.toLowerCase().includes(q) ||
            (p.description ?? '').toLowerCase().includes(q) ||
            prettyService(s.service).toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.permissions.length > 0);
  }, [catalog.services, q]);

  const totalAvailable = useMemo(
    () => catalog.services.reduce((n, s) => n + s.permissions.length, 0),
    [catalog.services],
  );

  const toggleOne = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  const toggleService = (keys: string[], allOn: boolean) => {
    const next = new Set(selected);
    keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
    onChange(next);
  };

  const toggleCollapse = (service: string) => {
    const next = new Set(collapsed);
    if (next.has(service)) next.delete(service);
    else next.add(service);
    setCollapsed(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-cream border border-border flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search permissions…"
            className="bg-transparent outline-none text-sm w-full text-foreground"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{selected.size}</span> of {totalAvailable}{' '}
          selected
        </span>
      </div>

      <div className="max-h-[46vh] overflow-y-auto rounded-[12px] border border-border divide-y divide-border">
        {services.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No permissions match “{query}”.
          </div>
        ) : (
          services.map((s) => {
            const keys = s.permissions.map((p) => p.key);
            const selCount = keys.filter((k) => selected.has(k)).length;
            const allOn = selCount === keys.length && keys.length > 0;
            const someOn = selCount > 0 && !allOn;
            const isCollapsed = collapsed.has(s.service);
            return (
              <div key={s.service} className="bg-card">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/40">
                  <button
                    type="button"
                    onClick={() => toggleCollapse(s.service)}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  <Checkbox
                    checked={allOn ? true : someOn ? 'indeterminate' : false}
                    onCheckedChange={() => toggleService(keys, allOn)}
                    aria-label={`Select all ${prettyService(s.service)} permissions`}
                  />
                  <span className="font-semibold text-sm text-foreground flex-1">
                    {prettyService(s.service)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {selCount}/{keys.length}
                  </span>
                </div>
                {!isCollapsed && (
                  <ul className="py-1">
                    {s.permissions.map((p) => (
                      <li key={p.key}>
                        <label className="flex items-start gap-3 px-4 py-2 hover:bg-muted/30 cursor-pointer">
                          <Checkbox
                            className="mt-0.5"
                            checked={selected.has(p.key)}
                            onCheckedChange={() => toggleOne(p.key)}
                            aria-label={p.key}
                          />
                          <span className="min-w-0">
                            <span className="block text-sm text-foreground font-mono">{p.key}</span>
                            {(p.description || p.dataScope) && (
                              <span className="block text-xs text-muted-foreground">
                                {p.description}
                                {p.dataScope ? (
                                  <span className="ml-1 italic">· scope: {p.dataScope}</span>
                                ) : null}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PermissionPicker;
